/**
 * Templates Feature Types
 *
 * Type definitions for the templates feature including:
 * - Template data structures
 * - Section types
 * - Module types
 * - UI state types
 */

import type {
  AdminPublicTemplate,
  AdminSection,
  AdminUserTemplate,
  AvailableModule,
  ModuleMappings,
  UserTemplateItem,
} from '../../types/types';

// Re-export commonly used types for convenience
export type {
  AdminPublicTemplate,
  AdminSection,
  AdminUserTemplate,
  AvailableModule,
  ModuleMappings,
  UserTemplateItem,
};

/**
 * Template types based on ownership/source
 */
export type TemplateType = 'user' | 'public' | 'group';

/**
 * Template source when creating new templates
 */
export type TemplateSource = 'scratch' | 'library' | 'upload';

/**
 * View modes for the templates page
 */
export type TemplatesViewMode =
  | 'empty' // No template selected, showing welcome view
  | 'editor' // Editing a selected template
  | 'library' // Browsing template library
  | 'manage-sections'; // Managing custom sections

/**
 * Template with computed properties for UI
 */
export interface TemplateWithMeta extends UserTemplateItem {
  /** Whether the template can be modified */
  canModify: boolean;
  /** Whether the template can be copied/duplicated */
  canDuplicate: boolean;
  /** Whether the template can be deleted */
  canDelete: boolean;
  /** Display icon type */
  iconType: 'user' | 'public' | 'group';
}

/**
 * Loaded template data (either user or public template)
 */
export type LoadedTemplate = AdminUserTemplate | AdminPublicTemplate;

/**
 * Section with UI-specific metadata
 */
export interface SectionWithMeta extends AdminSection {
  /** Module information from available modules */
  moduleInfo?: AvailableModule;
  /** Whether this section is customized */
  isCustomized: boolean;
  /** Whether preview is expanded */
  showPreview?: boolean;
}

/**
 * Props for the main Templates page
 */
export interface TemplatesPageProps {
  /** Initial template ID to select (from URL params) */
  initialTemplateId?: string;
}

/**
 * Template editor state
 */
export interface TemplateEditorState {
  /** The template being edited */
  template: LoadedTemplate | null;
  /** Whether template is loading */
  isLoading: boolean;
  /** Whether template has unsaved changes */
  hasChanges: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Whether title is in edit mode */
  isTitleEditing: boolean;
  /** Whether module selector is open */
  isModuleSelectorOpen: boolean;
  /** Index where new section should be added */
  addSectionIndex: number | null;
}

/**
 * Template sidebar state
 */
export interface TemplateSidebarState {
  /** List of user templates */
  templates: UserTemplateItem[];
  /** Currently selected template ID */
  selectedTemplateId: string | null;
  /** Whether sidebar is loading */
  isLoading: boolean;
  /** Current view mode */
  viewMode: TemplatesViewMode;
}

/**
 * Template library filter options
 */
export type LibraryFilterOption = 'all' | 'behavioral-health' | 'medical' | 'custom';

/**
 * Template library state
 */
export interface TemplateLibraryState {
  /** Available public templates */
  publicTemplates: AdminPublicTemplate[];
  /** Search term */
  searchTerm: string;
  /** Selected category filter */
  categoryFilter: string | null;
  /** Whether library is loading */
  isLoading: boolean;
}

/**
 * Analytics event metadata for templates
 */
export interface TemplateAnalyticsMetadata {
  template_id?: string;
  template_type?: TemplateType;
  template_name?: string;
  source?: TemplateSource;
  section_count?: number;
  action?: string;
}

/**
 * Type guard for AdminPublicTemplate
 */
export const isPublicTemplate = (
  template: LoadedTemplate | null | undefined,
): template is AdminPublicTemplate => {
  return template !== null && template !== undefined && 'public_name' in template;
};

/**
 * Type guard for AdminUserTemplate
 */
export const isUserTemplate = (
  template: LoadedTemplate | null | undefined,
): template is AdminUserTemplate => {
  return template !== null && template !== undefined && !('public_name' in template);
};
